// src/components/Admin/TramitesAdmin.jsx
import React, { useEffect, useState, useRef } from "react";
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField,
    Typography, IconButton, Select, MenuItem, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TablePagination,
} from "@mui/material";
import { Add, Delete, Edit } from "@mui/icons-material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import axios from "axios";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import RichTextEditor from "../Utiles/RichTextEditor.jsx";
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";

/* ----------------- helpers fechas ----------------- */
function formatFechaLargaES(date = new Date()) {
    const meses = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
    const dia = date.getDate();
    const mes = meses[date.getMonth()];
    const anio = date.getFullYear();
    return `Valladolid, a ${dia} de ${mes} de ${anio}`;
}
function fmtDateISO(d) {
    if (!d) return "";
    try {
        const dd = new Date(d);
        if (Number.isNaN(dd.getTime())) return String(d);
        return dd.toISOString().slice(0, 10);
    } catch { return String(d); }
}

/* ---------- normalizadores ---------- */
const normTramite = (t) => ({
    id: t.id, nombre: t.nombre, descripcion: t.descripcion,
    numArchivos: Number(t.numArchivos ?? t.num_archivos ?? 0),
});
const normRelacion = (r) => ({
    colegiadoCodigo: r.colegiadoCodigo ?? r.colegiado_codigo ?? r.colegiadoId ?? r.colegiado_id ?? null,
    tramiteId: r.tramiteId ?? r.tramite_id ?? null,
    tramiteNombre: r.tramiteNombre ?? r.tramite_nombre ?? "",
    estado: r.estado ?? "PENDIENTE",
});
const normPage = (p) => ({
    content: Array.isArray(p?.content) ? p.content : [],
    number: p?.number ?? p?.page ?? 0,
    size: p?.size ?? p?.page_size ?? 10,
    totalPages: p?.totalPages ?? p?.total_pages ?? 0,
    totalElements: p?.totalElements ?? p?.total_elements ?? 0,
});
const fileKey = (f) => `${f.name}__${f.size}__${f.lastModified}`;

/* ======== detectar si un trámite es "baja" y su tipo ======== */
function esTramiteBaja(nombre = "") {
    const s = String(nombre || "").toLowerCase();
    return s.includes("baja");
}
function tipoBajaDesdeNombre(nombre = "") {
    const s = String(nombre || "").toLowerCase();
    if (s.includes("jubilacion") || s.includes("jubilación")) return "Baja por Jubilación";
    if (s.includes("traslado")) return "Baja por traslado";
    return "Baja";
}

export default function TramitesAdmin() {
    const API = process.env.REACT_APP_API_URL || "http://localhost:8080";
    const PUBLIC_BASE = API.replace(/\/api\/?$/, "");
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };
    const headersAuth = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });
    const navigate = useNavigate();

    const toPublicUrl = (serverUrl) => (serverUrl?.startsWith("http") ? serverUrl : `${PUBLIC_BASE}${serverUrl}`);
    const absolutizePublicLinks = (html) => (html || "").replace(/href="\/public\//g, `href="${PUBLIC_BASE}/public/`);

    // ------ catálogo de tramites ------
    const [tramites, setTramites] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [editando, setEditando] = useState(null);
    const [plantillas, setPlantillas] = useState([]);
    const [formData, setFormData] = useState({ nombre: "", descripcion: "", numArchivos: 1 });

    // ver descripción
    const [modalDescripcion, setModalDescripcion] = useState(false);
    const [tramiteSeleccionado, setTramiteSeleccionado] = useState(null);

    // ------ relaciones colegiados_tramites ------
    const [rowsPage, setRowsPage] = useState({ content: [], number: 0, size: 10, totalPages: 0, totalElements: 0 });
    const [loadingRel, setLoadingRel] = useState(false);
    const [estadosLocal, setEstadosLocal] = useState({});
    const [filtroTramiteId, setFiltroTramiteId] = useState("");
    const [filtroEstado, setFiltroEstado] = useState("");
    const [filtroColegiado, setFiltroColegiado] = useState("");

    // ========= cache + nombres completos =========
    const colegiadoCache = useRef(new Map()); // codigo -> {nombre, apellido_1, apellido_2, ...} | null
    const [nombres, setNombres] = useState({}); // codigo -> "Nombre Ap1 Ap2"

    const displayFullName = (c) => {
        const parts = [c?.nombre, c?.apellido_1, c?.apellido_2].filter(Boolean);
        return parts.join(" ").trim();
    };

    // -------- función de detalles (con cache) --------
    const fetchColegiadoDetalles = async (codigoColegiado) => {
        if (!codigoColegiado) return null;
        if (colegiadoCache.current.has(codigoColegiado))
            return colegiadoCache.current.get(codigoColegiado);

        try {
            const { data } = await axios.get(`${API}/api/admin/colegiados/${codigoColegiado}`, {
                headers: headersAuth(),
            });
            const c = {
                nombre: data?.nombre ?? "",
                apellido_1: data?.apellido_1 ?? "",
                apellido_2: data?.apellido_2 ?? "",
                localidad: data?.localidad ?? "",
                provincia: data?.provincia ?? "",
            };
            colegiadoCache.current.set(codigoColegiado, c);
            return c;
        } catch {
            colegiadoCache.current.set(codigoColegiado, null);
            return null;
        }
    };

    // Prefetch nombres para la página actual
    useEffect(() => {
        const prefetch = async () => {
            const codigos = Array.from(new Set((rowsPage.content || []).map(r => r.colegiadoCodigo).filter(Boolean)));
            if (!codigos.length) return;
            const updates = {};
            await Promise.allSettled(codigos.map(async (cod) => {
                const det = await fetchColegiadoDetalles(cod);
                updates[cod] = det ? displayFullName(det) : "";
            }));
            // merge sin perder lo anterior
            setNombres(prev => ({ ...prev, ...updates }));
        };
        prefetch();
    }, [rowsPage.content]);

    // ========= Hacer trámite por colegiado =========
    const [openHacer, setOpenHacer] = useState(false);
    const [hacerCodigo, setHacerCodigo] = useState("");
    const [hacerTramiteId, setHacerTramiteId] = useState("");
    const [hacerEstado, setHacerEstado] = useState("PENDIENTE");
    const [hacerArchivos, setHacerArchivos] = useState([]);

    // ========= Finalizar/Enviar BAJA =========
    const [openFinalizar, setOpenFinalizar] = useState(false);
    const [rowFinalizar, setRowFinalizar] = useState(null); // { colegiadoCodigo, tramiteId, tramiteNombre }
    const [numResolucion, setNumResolucion] = useState("");
    const [finalizarModo, setFinalizarModo] = useState("descargar"); // "descargar" | "enviar"

    // =================== fetch catálogo ===================
    const fetchTramites = async () => {
        try {
            const res = await axios.get(`${API}/api/tramites`, { headers });
            const list = Array.isArray(res.data) ? res.data.map(normTramite) : [];
            setTramites(list);
        } catch {
            setTramites([]);
        }
    };
    useEffect(() => {
        fetchTramites();
    }, []);

    // =================== CRUD tramites ===================
    const handleOpen = (tramite = null) => {
        if (tramite) {
            const n = normTramite(tramite);
            setEditando(n.id);
            setFormData({ nombre: n.nombre, descripcion: n.descripcion, numArchivos: n.numArchivos });
        } else {
            setEditando(null);
            setFormData({ nombre: "", descripcion: "", numArchivos: 1 });
            setPlantillas([]);
        }
        setOpenDialog(true);
    };
    const handleClose = () => { setOpenDialog(false); setPlantillas([]); };
    const handleChange = (e) => setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));
    const handleSubmit = async () => {
        const payload = { nombre: formData.nombre, descripcion: absolutizePublicLinks(formData.descripcion), num_archivos: Number(formData.numArchivos) };
        try {
            if (editando) await axios.put(`${API}/api/tramites/${editando}`, payload, { headers });
            else await axios.post(`${API}/api/tramites`, payload, { headers });
            await fetchTramites();
            handleClose();
        } catch {
            alert("Error guardando el trámite");
        }
    };
    const handleEliminar = async (id) => {
        if (!window.confirm("¿Eliminar este trámite?")) return;
        try { await axios.delete(`${API}/api/tramites/${id}`, { headers }); await fetchTramites(); }
        catch { alert("No se pudo eliminar."); }
    };
    const handleVerDescripcion = (t) => { setTramiteSeleccionado(normTramite(t)); setModalDescripcion(true); };

    // =================== Plantillas públicas (PDF) ===================
    const handlePlantillasChange = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        const uploadAxios = axios.create();
        try {
            const nombreSubv = (formData.nombre ?? "Subvencion").trim();
            const promesas = files.map(async (f) => {
                if (f.type !== "application/pdf") throw new Error(`El archivo ${f.name} no es un PDF`);
                const fd = new FormData();
                fd.append("file", f, f.name);
                fd.append("nombreSubvencion", nombreSubv);
                const res = await uploadAxios.post(`${API}/api/documentos/admin/plantillas`, fd, { headers, transformRequest: (d) => d });
                return res.data; // { url, filename }
            });
            const nuevas = await Promise.all(promesas);
            setPlantillas((prev) => [...prev, ...nuevas]);
        } catch (err) {
            alert(err.message || "Error subiendo plantillas");
        } finally { e.target.value = null; }
    };

    // =================== Relaciones (paginado + filtro + export) ===================
    const cargarRelaciones = async (page = 0, size = rowsPage.size) => {
        setLoadingRel(true);
        try {
            const res = await axios.get(`${API}/api/colegiados-tramites/page`, {
                params: {
                    page, size,
                    colegiado: filtroColegiado || undefined,
                    tramite_id: filtroTramiteId || undefined,
                    estado: filtroEstado || undefined,
                },
                headers,
            });
            const p = normPage(res.data);
            const contentNorm = (p.content || []).map(normRelacion);
            setRowsPage({ ...p, content: contentNorm });
        } catch {
            setRowsPage(normPage({ content: [], number: 0, size, totalPages: 0, totalElements: 0 }));
        } finally { setLoadingRel(false); }
    };

    const handleChangeEstadoLocal = (codigo, tramiteId, nuevo) => {
        setEstadosLocal((prev) => ({ ...prev, [`${codigo}_${tramiteId}`]: nuevo }));
    };

    const actualizarEstado = async (codigo, tramiteId, estadoForzado = null) => {
        const estadoSeleccionado = estadoForzado ?? (estadosLocal[`${codigo}_${tramiteId}`] || "PENDIENTE");
        await axios.put(
            `${API}/api/colegiados/${codigo}/tramites/${tramiteId}/estado`,
            { estado: estadoSeleccionado },
            { headers }
        );
    };

    const exportarExcel = async () => {
        try {
            const size = 1000; let page = 0; let todos = [];
            for (let i = 0; i < 200; i++) {
                const res = await axios.get(`${API}/api/colegiados-tramites/page`, {
                    params: { page, size, colegiado: filtroColegiado || undefined, tramite_id: filtroTramiteId || undefined, estado: filtroEstado || undefined },
                    headers,
                });
                const p = normPage(res.data);
                const contentNorm = (p.content || []).map(normRelacion);
                todos = todos.concat(contentNorm);
                if (page + 1 >= p.totalPages) break; page++;
            }
            // Añadimos "Nombre completo" usando el cache si está disponible
            const rows = await Promise.all(todos.map(async (r) => {
                const cod = r.colegiadoCodigo ?? "";
                let nombreCompleto = nombres[cod];
                if (nombreCompleto === undefined) {
                    const det = await fetchColegiadoDetalles(cod);
                    nombreCompleto = det ? displayFullName(det) : "";
                }
                return {
                    Código: cod,
                    "Nombre completo": nombreCompleto || "",
                    Trámite: r.tramiteNombre ?? "",
                    Estado: r.estado ?? "",
                };
            }));

            const wb = XLSX.utils.book_new(); const ws = XLSX.utils.json_to_sheet(rows);
            XLSX.utils.book_append_sheet(wb, ws, "Coleg_Trámites");
            XLSX.writeFile(wb, `colegiados_tramites_${filtroTramiteId || "todos"}_${filtroEstado || "todos"}.xlsx`);
        } catch {
            alert("No se pudo exportar a Excel.");
        }
    };

    // =================== Hacer trámite por colegiado ===================
    const abrirHacer = () => { setHacerCodigo(""); setHacerTramiteId(""); setHacerEstado("PENDIENTE"); setHacerArchivos([]); setOpenHacer(true); };
    const cerrarHacer = () => setOpenHacer(false);
    const onHacerFiles = (e) => {
        const nuevos = Array.from(e.target.files || []);
        setHacerArchivos((prev) => {
            const mapa = new Map(prev.map((f) => [fileKey(f), f]));
            for (const f of nuevos) mapa.set(fileKey(f), f);
            return Array.from(mapa.values());
        });
        e.target.value = "";
    };
    const subirArchivosParaColegiado = async (codigo, tramiteNombre) => {
        if (!hacerArchivos.length) return;
        const nombreBase = (tramiteNombre || "Tramite").trim().replace(/\s+/g, "_");
        for (let i = 0; i < hacerArchivos.length; i++) {
            const archivo = hacerArchivos[i];
            const ext = archivo.name.includes(".") ? archivo.name.split(".").pop() : "";
            const nuevoNombre = ext ? `${nombreBase}_${i + 1}.${ext}` : `${nombreBase}_${i + 1}`;
            let payloadFile = archivo;
            try { payloadFile = new File([archivo], nuevoNombre, { type: archivo.type }); } catch { }
            const fd = new FormData();
            fd.append("file", payloadFile);
            fd.append("colegiadoId", String(codigo));
            await axios.post(`${API}/api/documentos/admin/subir`, fd, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
        }
    };
    const crearTramiteParaColegiado = async () => {
        const codigo = parseInt(hacerCodigo, 10);
        if (!codigo || Number.isNaN(codigo)) { alert("Indica un código de colegiado válido."); return; }
        if (!hacerTramiteId) { alert("Selecciona un trámite."); return; }
        const t = tramites.find((x) => String(x.id) === String(hacerTramiteId));
        const requeridos = Number(t?.numArchivos ?? 0);
        if (requeridos > 0 && hacerArchivos.length < requeridos) {
            alert(`Este trámite requiere al menos ${requeridos} archivo(s).`); return;
        }
        try {
            await subirArchivosParaColegiado(codigo, t?.nombre);
            try {
                await axios.post(`${API}/api/colegiados/${codigo}/tramites/${hacerTramiteId}`, {}, { params: { estado: hacerEstado }, headers: { ...headers, "Content-Type": "application/json" } });
            } catch (e) {
                if (e?.response?.status === 403) {
                    await axios.post(`${API}/api/colegiados-tramites/create`, { colegiado: codigo, tramiteId: Number(hacerTramiteId), estado: hacerEstado || "PENDIENTE" }, { headers: { ...headers, "Content-Type": "application/json" } });
                } else { throw e; }
            }
            alert("Trámite creado para el colegiado.");
            cerrarHacer();
            setFiltroColegiado(String(codigo));
            setFiltroTramiteId(String(hacerTramiteId));
            setFiltroEstado(hacerEstado || "");
            await cargarRelaciones(0);
        } catch (err) {
            const status = err?.response?.status;
            if (status === 409) alert("Ya existe una solicitud para ese trámite y colegiado.");
            else if (status === 404) alert("No se encontró el colegiado o el trámite.");
            else if (status === 403) alert("Permisos insuficientes (403) en la creación del trámite.");
            else alert("Error creando el trámite para el colegiado.");
        }
    };

    /* =================== Finalizar/Enviar Baja =================== */
    const abrirFinalizarBaja = (row, modo = "descargar") => {
        setRowFinalizar(row);
        setNumResolucion("");
        setFinalizarModo(modo); // "descargar" | "enviar"
        setOpenFinalizar(true);
    };
    const cerrarFinalizarBaja = () => {
        setOpenFinalizar(false);
        setRowFinalizar(null);
        setNumResolucion("");
        setFinalizarModo("descargar");
    };

    // Genera el PDF (Blob) y devuelve { blob, nombreBase }
    const generarPdfBajaBlob = async (row, numeroResolucion) => {
        // 1) Detalle del colegiado
        const det = await axios.get(
            `${API}/colegiado/admin/colegiados/${encodeURIComponent(row.colegiadoCodigo)}/detalle`,
            { headers }
        );
        const c = det?.data || {};
        const nombre = (c.nombre || c.name || "").toString().trim();
        const ap1 = (c.apellido_1 || c.apellido1 || "").toString().trim();
        const ap2 = (c.apellido_2 || c.apellido2 || "").toString().trim();
        const displayName = [nombre, ap1, ap2].filter(Boolean).join(" ");
        const nif = c.nif || c.dni || "";
        const codigo = c.codigo || c.id || row.colegiadoCodigo;
        const direccion = c.direccion || c.domicio || c.domicilio || "";
        const cp = c.c_p_ || c.cp || c.codigo_postal || "";
        const provincia = c.provincia || c.provinciaNombre || "";
        const fechaPrimeraCol = c.fecha_1a_Colegiacion || c.fecha_1a_colegiacion || c.fecha1aColegiacion || "";
        const lugarNacimiento = c.localidadNacimiento || c.localidad_nacimiento || c.lugar_nacimiento || "";
        const tipoBaja = tipoBajaDesdeNombre(row.tramiteNombre);
        const fechaCompleta = formatFechaLargaES(new Date());

        // 2) Plantilla
        const resp = await fetch("./public/certificados/Resolucion_baja.docx", { cache: "no-store" });
        if (!resp.ok) throw new Error("No se encontró la plantilla en /public/certificados/Resolucion_baja.docx");
        const arrayBuffer = await resp.arrayBuffer();
        const zip = new PizZip(arrayBuffer);

        // 3) Rellenar
        const data = {
            NUM_RESOLUCION: String(numeroResolucion),
            NOMBRE_COMPLETO: displayName,
            CODIGO: String(codigo),
            NIF: String(nif),
            DOMICILIO: direccion,
            PROVINCIA: provincia,
            C_P: String(cp),
            FECHA_1A_COLEGIACION: fmtDateISO(fechaPrimeraCol),
            LOCALIDAD_NACIMIENTO: lugarNacimiento || "",
            TIPO_BAJA: tipoBaja,
            FECHA_COMPLETA: fechaCompleta,
        };
        const doc = new Docxtemplater(zip, { paragraphLoop: true, linebreaks: true, delimiters: { start: "{", end: "}" } });
        doc.setData(data); doc.render();
        const docxBlob = doc.getZip().generate({ type: "blob", mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });

        // 4) DOCX -> PDF
        const form = new FormData();
        const nombreBase = `Resolucion_Baja_${codigo}_${(displayName || "Colegiado").replace(/\s+/g, "_")}`;
        form.append("file", docxBlob, `${nombreBase}.docx`);
        const pdfResp = await axios.post(`${API}/api/convert/docx-to-pdf`, form, {
            headers: { ...headers, "Content-Type": "multipart/form-data" },
            responseType: "blob",
        });
        const pdfBlob = new Blob([pdfResp.data], { type: "application/pdf" });
        return { blob: pdfBlob, nombreBase };
    };

    const onConfirmarFinalizarBaja = async () => {
        if (!rowFinalizar?.colegiadoCodigo) { alert("Falta el código de colegiado."); return; }
        if (!numResolucion || !String(numResolucion).trim()) { alert("Indica el número de resolución/registro."); return; }

        try {
            const { blob, nombreBase } = await generarPdfBajaBlob(rowFinalizar, numResolucion);

            if (finalizarModo === "descargar") {
                // Descargar
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = `${nombreBase}.pdf`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);

                // AJUSTE: cambiar estado a FINALIZADA
                await actualizarEstado(rowFinalizar.colegiadoCodigo, rowFinalizar.tramiteId, "FINALIZADA");
                await cargarRelaciones(rowsPage.number, rowsPage.size);
                alert("PDF generado y solicitud marcada como FINALIZADA.");
            } else {
                // Enviar (subir a Documentos del colegiado)
                const file = new File([blob], `${nombreBase}.pdf`, { type: "application/pdf" });
                const fd = new FormData();
                fd.append("file", file);
                fd.append("colegiadoId", String(rowFinalizar.colegiadoCodigo));
                await axios.post(`${API}/api/documentos/admin/subir`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });

                alert("Documento enviado al colegiado correctamente.");
            }

            cerrarFinalizarBaja();
        } catch (e) {
            console.error(e);
            alert(e?.response?.data || e?.message || "No se pudo generar/enviar la Resolución de Baja.");
        }
    };

    /* =================== navegación a Documentos del colegiado =================== */
    const irADocumentosColegiado = (colegiadoCodigo) => {
        // Ajusta la ruta si tu componente documentos admin vive en otra URL.
        navigate(`/admin/documentos?colegiado=${encodeURIComponent(colegiadoCodigo)}`);
    };

    // =================== render ===================
    return (
        <Paper elevation={3} sx={{ padding: 3, margin: 2 }}>
            <Box p={3}>
                {/* Cabecera y botones */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h5">Gestión de Trámites</Typography>
                    <Box display="flex" gap={1}>
                        <Button variant="outlined" onClick={() => { setOpenHacer(true); }}>Hacer trámite por colegiado</Button>
                        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>Nuevo Trámite</Button>
                    </Box>
                </Box>

                {/* Listado simple de trámites */}
                {tramites.length === 0 ? (
                    <Typography>No hay trámites registrados.</Typography>
                ) : (
                    tramites.map((t0) => {
                        const t = normTramite(t0);
                        return (
                            <Box key={t.id} className="border rounded p-3 mb-3">
                                <div className="flex flex-col space-y-1">
                                    <div className="flex gap-2">
                                        <IconButton onClick={() => handleOpen(t)} size="small" title="Editar"><Edit fontSize="small" /></IconButton>
                                        <IconButton onClick={() => handleEliminar(t.id)} size="small" title="Eliminar"><Delete fontSize="small" /></IconButton>
                                        <IconButton onClick={() => handleVerDescripcion(t)} size="small" title="Ver descripción"><VisibilityIcon fontSize="small" /></IconButton>
                                    </div>

                                    <Typography className="font-semibold text-base">
                                        {t.nombre}
                                        <span className="ml-2 text-sm text-gray-500 font-normal">Requiere {t.numArchivos} archivo(s)</span>
                                    </Typography>

                                    <div className="tiptap-rendered max-h-24 overflow-hidden text-sm" dangerouslySetInnerHTML={{ __html: t.descripcion }} />
                                </div>
                            </Box>
                        );
                    })
                )}

                {/* Diálogo crear/editar */}
                <Dialog open={openDialog} onClose={handleClose} fullWidth maxWidth="md">
                    <DialogTitle>{editando ? "Editar" : "Nuevo"} Trámite</DialogTitle>
                    <DialogContent>
                        <TextField label="Nombre" name="nombre" value={formData.nombre} onChange={handleChange} fullWidth margin="dense" />
                        <RichTextEditor value={formData.descripcion} onChange={(val) => setFormData((p) => ({ ...p, descripcion: val }))} />
                        <TextField label="Nº Archivos Requeridos" name="numArchivos" type="number" value={formData.numArchivos} onChange={handleChange} fullWidth margin="dense" inputProps={{ min: 0 }} />
                        {/* Plantillas públicas */}
                        <Box mt={2}>
                            <Typography variant="subtitle2">Plantillas públicas (PDF)</Typography>
                            <input type="file" accept="application/pdf" multiple onChange={handlePlantillasChange} />
                            <Box mt={1}>
                                {plantillas.map((p, idx) => (
                                    <Box key={idx} display="flex" alignItems="center" gap={1} mb={0.5}>
                                        <Typography variant="body2">{p.filename}</Typography>
                                        <Button size="small" variant="outlined" onClick={() => {
                                            const enlace = `<a href="${toPublicUrl(p.url)}" target="_blank" rel="noopener noreferrer">${p.filename}</a>`;
                                            setFormData((prev) => ({ ...prev, descripcion: (prev.descripcion || "") + `<p>${enlace}</p>` }));
                                        }}>Insertar en descripción</Button>
                                    </Box>
                                ))}
                            </Box>
                            {plantillas.length > 0 && (
                                <Button size="small" sx={{ mt: 1 }} onClick={() => {
                                    const lista = plantillas.map((p) => `<li><a href="${toPublicUrl(p.url)}" target="_blank" rel="noopener noreferrer">${p.filename}</a></li>`).join("");
                                    setFormData((prev) => ({ ...prev, descripcion: (prev.descripcion || "") + `<ul>${lista}</ul>` }));
                                }}>Insertar todos en descripción</Button>
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose}>Cancelar</Button>
                        <Button variant="contained" onClick={handleSubmit}>Guardar</Button>
                    </DialogActions>
                </Dialog>

                {/* RELACIONES */}
                <Box mt={4}>
                    <Box display="flex" gap={1} alignItems="center" mb={1}>
                        <Button variant="outlined" onClick={() => cargarRelaciones()}>Ver solicitudes de trámites</Button>

                        <Select size="small" value={filtroTramiteId} onChange={(e) => { setFiltroTramiteId(e.target.value); setRowsPage((p) => ({ ...p, number: 0 })); }} displayEmpty sx={{ minWidth: 260 }}>
                            <MenuItem value=""><em>Todos los trámites</em></MenuItem>
                            {tramites.map((t) => (<MenuItem key={t.id} value={t.id}>{t.nombre}</MenuItem>))}
                        </Select>

                        <Select size="small" value={filtroEstado} onChange={(e) => { setFiltroEstado(e.target.value); setRowsPage((p) => ({ ...p, number: 0 })); }} sx={{ minWidth: 180 }} displayEmpty>
                            <MenuItem value=""><em>Todos los estados</em></MenuItem>
                            <MenuItem value="PENDIENTE">PENDIENTE</MenuItem>
                            <MenuItem value="FINALIZADA">FINALIZADA</MenuItem>
                            <MenuItem value="RECHAZADA">RECHAZADA</MenuItem>
                        </Select>

                        <TextField size="small" label="Código colegiado" value={filtroColegiado} onChange={(e) => setFiltroColegiado(e.target.value)} sx={{ width: 180 }} />

                        <Button variant="contained" onClick={() => cargarRelaciones(0)}>Aplicar filtro</Button>
                        <Button onClick={exportarExcel}>Exportar Excel</Button>
                    </Box>

                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell style={{ width: 220 }}>Código · Nombre completo</TableCell>
                                    <TableCell>Trámite</TableCell>
                                    <TableCell style={{ width: 200 }}>Estado</TableCell>
                                    <TableCell style={{ width: 120 }}>Documentos</TableCell>
                                    <TableCell align="right" style={{ width: 360 }}>Acciones</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loadingRel ? (
                                    <TableRow><TableCell colSpan={5}><Typography>Cargando…</Typography></TableCell></TableRow>
                                ) : rowsPage.content?.length === 0 ? (
                                    <TableRow><TableCell colSpan={5}><Typography>No hay solicitudes.</Typography></TableCell></TableRow>
                                ) : (
                                    rowsPage.content?.map((r) => {
                                        const esBaja = esTramiteBaja(r.tramiteNombre);
                                        const cod = r.colegiadoCodigo ?? "";
                                        const nombreCompleto = nombres[cod];
                                        return (
                                            <TableRow key={`${r.colegiadoCodigo}_${r.tramiteId}`}>
                                                <TableCell>
                                                    <div style={{ display: "flex", flexDirection: "column" }}>
                                                        <span><strong>{cod}</strong></span>
                                                        <span style={{ fontSize: 12, opacity: 0.85 }}>
                                                            {nombreCompleto === undefined ? "…" : (nombreCompleto || "(sin nombre)")}
                                                        </span>
                                                    </div>
                                                </TableCell>
                                                <TableCell>{r.tramiteNombre ?? ""}</TableCell>
                                                <TableCell>
                                                    <Select
                                                        size="small"
                                                        value={estadosLocal[`${r.colegiadoCodigo}_${r.tramiteId}`] ?? r.estado ?? "PENDIENTE"}
                                                        onChange={(e) => handleChangeEstadoLocal(r.colegiadoCodigo, r.tramiteId, e.target.value)}
                                                        sx={{ minWidth: 180 }}
                                                    >
                                                        <MenuItem value="PENDIENTE">PENDIENTE</MenuItem>
                                                        <MenuItem value="FINALIZADA">FINALIZADA</MenuItem>
                                                        <MenuItem value="RECHAZADA">RECHAZADA</MenuItem>
                                                    </Select>
                                                </TableCell>

                                                {/* Enlace a Documentos del colegiado */}
                                                <TableCell>
                                                    <Button size="small" variant="text" onClick={() => irADocumentosColegiado(r.colegiadoCodigo)}>
                                                        Documentos
                                                    </Button>
                                                </TableCell>

                                                <TableCell align="right">
                                                    <Button size="small" variant="outlined" onClick={async () => {
                                                        try {
                                                            await actualizarEstado(r.colegiadoCodigo, r.tramiteId);
                                                            await cargarRelaciones(rowsPage.number, rowsPage.size);
                                                            alert("Estado actualizado");
                                                        } catch { alert("Error al actualizar estado"); }
                                                    }} sx={{ mr: 1 }}>
                                                        Guardar
                                                    </Button>

                                                    {esBaja && (
                                                        <>
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                onClick={() => abrirFinalizarBaja(r, "descargar")}
                                                                sx={{ mr: 1 }}
                                                            >
                                                                Finalizar
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                color="secondary"
                                                                onClick={() => abrirFinalizarBaja(r, "enviar")}
                                                            >
                                                                Enviar
                                                            </Button>
                                                        </>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        component="div"
                        count={rowsPage.totalElements}
                        page={rowsPage.number}
                        onPageChange={(_e, newPage) => cargarRelaciones(newPage, rowsPage.size)}
                        rowsPerPage={rowsPage.size}
                        onRowsPerPageChange={(e) => cargarRelaciones(0, parseInt(e.target.value, 10))}
                        rowsPerPageOptions={[5, 10, 20, 50, 100]}
                        labelRowsPerPage="Filas por página"
                        labelDisplayedRows={({ from, to, count }) => `${Number.isNaN(from) ? 0 : from}–${Number.isNaN(to) ? 0 : to} de ${count}`}
                    />
                </Box>
            </Box>

            {/* Modal ver descripción */}
            <Dialog open={modalDescripcion} onClose={() => setModalDescripcion(false)} fullWidth maxWidth="md">
                <DialogTitle>{tramiteSeleccionado?.nombre}</DialogTitle>
                <DialogContent dividers>
                    <div className="tiptap-rendered" dangerouslySetInnerHTML={{ __html: tramiteSeleccionado?.descripcion }} />
                </DialogContent>
                <DialogActions><Button onClick={() => setModalDescripcion(false)}>Cerrar</Button></DialogActions>
            </Dialog>

            {/* Modal Hacer trámite por colegiado */}
            <Dialog open={openHacer} onClose={() => setOpenHacer(false)} fullWidth maxWidth="sm">
                <DialogTitle>Hacer trámite por colegiado</DialogTitle>
                <DialogContent dividers>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                        <TextField label="Código colegiado" type="number" value={hacerCodigo} onChange={(e) => setHacerCodigo(e.target.value)} fullWidth />
                        <Select value={hacerTramiteId} onChange={(e) => setHacerTramiteId(e.target.value)} displayEmpty>
                            <MenuItem value=""><em>Selecciona trámite</em></MenuItem>
                            {tramites.map((t) => (
                                <MenuItem key={t.id} value={t.id}>
                                    {t.nombre} {t.numArchivos > 0 ? ` · (${t.numArchivos} archivos)` : ""}
                                </MenuItem>
                            ))}
                        </Select>
                        <Select value={hacerEstado} onChange={(e) => setHacerEstado(e.target.value)} displayEmpty>
                            <MenuItem value="PENDIENTE">PENDIENTE</MenuItem>
                            <MenuItem value="FINALIZADA">FINALIZADA</MenuItem>
                            <MenuItem value="RECHAZADA">RECHAZADA</MenuItem>
                        </Select>
                    </Box>

                    <Box mt={2}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            Adjunta los archivos para este trámite (se guardarán en la ficha del colegiado).
                        </Typography>
                        <input key={openHacer ? "hacer-open" : "hacer-closed"} type="file" multiple onChange={onHacerFiles} />
                        <Box mt={1}>
                            {hacerArchivos.map((f) => (
                                <Box key={fileKey(f)} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                                    <Typography variant="body2" sx={{ flex: 1 }} title={f.name}>
                                        {f.name} ({Math.round(f.size / 1024)} KB)
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                        {!!hacerTramiteId && (
                            <Typography variant="caption" color="text.secondary">
                                Requeridos: {(tramites.find((x) => String(x.id) === String(hacerTramiteId))?.numArchivos) ?? 0} archivo(s).
                            </Typography>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenHacer(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={crearTramiteParaColegiado}>Crear trámite</Button>
                </DialogActions>
            </Dialog>

            {/* Modal Finalizar/Enviar Baja (Nº resolución) */}
            <Dialog open={openFinalizar} onClose={cerrarFinalizarBaja} fullWidth maxWidth="xs">
                <DialogTitle>{finalizarModo === "enviar" ? "Enviar resolución de baja" : "Finalizar baja"}</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Introduce el Nº de resolución/registro que se plasmará en el documento.
                    </Typography>
                    <TextField fullWidth label="Nº de resolución" value={numResolucion} onChange={(e) => setNumResolucion(e.target.value)} autoFocus />
                    {rowFinalizar && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                            Colegiado: {rowFinalizar.colegiadoCodigo} · Trámite: {rowFinalizar.tramiteNombre}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={cerrarFinalizarBaja}>Cancelar</Button>
                    <Button variant="contained" onClick={onConfirmarFinalizarBaja}>
                        {finalizarModo === "enviar" ? "Generar y enviar" : "Generar PDF y finalizar"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
}
